import React, { useEffect, useRef } from 'react';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import { EditorShell } from './RichTextEditor.styles';

/**
 * Toolbar grouped like TinyMCE (Edit | Format | Insert | View).
 * SunEditor separates groups with adjacent arrays (not '|'); '/' is a line break.
 * Video insert is intentionally omitted.
 */
export const DEFAULT_BUTTON_LIST = [
  ['undo', 'redo'],
  ['font', 'fontSize', 'formatBlock'],
  ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
  ['fontColor', 'hiliteColor', 'removeFormat'],
  ['outdent', 'indent', 'align', 'list', 'lineHeight', 'horizontalRule'],
  ['table', 'link', 'image'],
  ['fullScreen', 'showBlocks', 'codeView', 'preview'],
];

/** Drop invalid TinyMCE-style '|' separators; SunEditor uses array groups instead. */
const sanitizeButtonList = (list: unknown) => {
  if (!Array.isArray(list)) return DEFAULT_BUTTON_LIST;
  const cleaned = list
    .map((group) => {
      if (group === '/') return '/';
      if (!Array.isArray(group)) return null;
      const buttons = group.filter((btn) => btn && btn !== '|');
      return buttons.length ? buttons : null;
    })
    .filter(Boolean);
  return cleaned.length ? cleaned : DEFAULT_BUTTON_LIST;
};

const DEFAULT_FONTS = [
  'Arial',
  'Comic Sans MS',
  'Courier New',
  'Georgia',
  'Helvetica',
  'Impact',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
];

export type RichTextEditorProps = {
  value?: string;
  handleChange?: (content: string) => void;
  height?: string | number;
  placeholder?: string;
  disabled?: boolean;
  buttonList?: unknown;
};

/**
 * Reusable rich-text editor (SunEditor) replacing TinyMCE.
 * Keeps the previous API: value, handleChange, height.
 */
const RichTextEditor = ({
  value = '',
  handleChange = () => {},
  height = '500',
  placeholder = '',
  disabled = false,
  buttonList = null,
}: RichTextEditorProps) => {
  const editorRef = useRef<any>(null);
  const lastExternalValue = useRef(value || '');

  const getSunEditorInstance = (sunEditor: any) => {
    editorRef.current = sunEditor;
  };

  // Sync when parent changes value from outside (e.g. load / reset), without fighting typing.
  useEffect(() => {
    const next = value || '';
    if (next === lastExternalValue.current) return;
    lastExternalValue.current = next;
    if (editorRef.current) {
      editorRef.current.setContents(next);
    }
  }, [value]);

  // Insert selected image files as data URLs (parity with TinyMCE file_picker_callback).
  const handleImageUploadBefore = (
    files: FileList | File[],
    _info: unknown,
    uploadHandler: (response?: { result: Array<{ url: string; name: string; size: number }> }) => void
  ) => {
    const file = files && (files as FileList)[0];
    if (!file) {
      uploadHandler();
      return undefined;
    }
    const reader = new FileReader();
    reader.onload = () => {
      uploadHandler({
        result: [
          {
            url: reader.result as string,
            name: file.name,
            size: file.size,
          },
        ],
      });
    };
    reader.onerror = () => uploadHandler();
    reader.readAsDataURL(file);
    return undefined;
  };

  return (
    <EditorShell>
      <SunEditor
        getSunEditorInstance={getSunEditorInstance}
        defaultValue={value || ''}
        onChange={(content) => {
          lastExternalValue.current = content;
          handleChange(content);
        }}
        disable={!!disabled}
        setOptions={{
          height: String(height || 500),
          minHeight: '160px',
          width: '100%',
          resizingBar: true,
          placeholder: placeholder || 'Start typing...',
          defaultTag: 'p',
          font: DEFAULT_FONTS,
          buttonList: sanitizeButtonList(buttonList || DEFAULT_BUTTON_LIST),
          formats: [
            'p',
            'div',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'blockquote',
            'pre',
          ],
          imageFileInput: true,
          imageUrlInput: true,
          videoFileInput: false,
          attributesWhitelist: {
            all: 'style|data-.+',
          },
        }}
        onImageUploadBefore={handleImageUploadBefore}
      />
    </EditorShell>
  );
};

export default RichTextEditor;
